import { FieldNames } from 'app/models/fields/names';
import {
  ACCOUNT_TYPE_PURCHASE_OPTIONS,
  COBORROWER_EMPLOYMENT_TYPE_OPTIONS,
  EMPLOYMENT_TYPE_OPTIONS,
  LIVING_SITUATION_OPTIONS,
  REAL_ESTATE_LIABILITY_OPTIONS,
  TURBO_BUYING_PROCESS_OPTIONS,
  YES_OR_NO_OPTIONS,
} from 'app/models/options/options';
import { Currency } from 'app/components/FormFields/Currency';
import { Select } from 'app/components/FormFields/Select';
import { RadioField } from 'app/components/FormFields/RadioField';
import { INCOME_DISCLAIMER, SHARED } from 'app/models/questions/shared';
import { Questions } from './Questions';
import { Address } from '../../components/FormFields/Address';
import { State } from '../../components/FormFields/State';
import { ZipCode } from '../../components/FormFields/ZipCode';
import { TextInput } from '../../components/FormFields/TextInput';
import {
  coBorrowerHasDifferentAddress,
  hasCoBorrower,
  isCoBorrowerEmploymentOther,
  isCoBorrowerRetired,
  isCoLessThanTwoYrsAtJob,
  isEmploymentOther,
  isLessThanTwoYrsAtJob,
  isLessThanTwoYrsAtLivingAddress,
  isRetired,
  hasSignedAgreement,
  hasOfferPending,
  isShopping,
  isHomeowner,
  hasShowAgesOfDependents,
  hasAddlProperties, isCoEmployee, isCoSelfEmployed, hasFinancialVerificationIntegration,
} from '../fields/conditionals';
import { SinglePastDate } from '../../components/FormFields/SinglePastDate';
import { Name } from '../../components/FormFields/Name';
import { LoanOfficerLanding } from '../../components/LoanOfficerLanding';
import { IncomeSources } from '../../components/FormFieldArrays/IncomeSources';
import { RetirementIncomeSources } from '../../components/FormFieldArrays/RetirementIncomeSources';
import { OtherIncomeSources } from '../../components/FormFieldArrays/OtherIncomeSources';
import { SingleDate } from '../../components/FormFields/SingleDate';
import { CollectRequiredSsn } from '../../components/FormFields/Ssn/CollectRequiredSsn';
import {
  PURCHASE_TURBO_COLLECT_SSN_QUESTION,
  PURCHASE_TURBO_COLLECT_SSN_QUESTION_FINAL,
  PURCHASE_TURBO_IS_SHOPPING_ZIP_CODE_QUESTION,
} from '../sections/constants';
import { CollectConditionallyRequiredSsn } from '../../components/FormFields/Ssn/CollectConditionallyRequiredSsn';
import { PurchaseDownPayment } from '../../components/FormFields/PurchaseDownPayment';
import { SingleFutureDate } from '../../components/FormFields/SingleFutureDate';
import { Year } from '../../components/FormFields/Year';
import { AgesFFAComponent } from '../../components/FormFieldArrays/Dynamic/Ages';
import { AdditionalProperties } from '../../components/FormFieldArrays/AdditionalProperties';
import { AssetSummary } from '../../components/AssetSummary';
import { BankAccounts } from '../../components/FormFieldArrays/BankAccounts';
import { ReoLiabilities, FinancialVerificationConnection } from 'app/components/Turbo';

export const PURCHASE_TURBO_QUESTIONS: Questions = new Questions([
  {
    id       : 1001,
    component: RadioField,
    name     : FieldNames.buyingProcess,
    title    : 'Where are you in the buying process?',
    options: TURBO_BUYING_PROCESS_OPTIONS,
  },
  {
    id         : 1002,
    component  : PurchaseDownPayment,
    hideTitle  : true,
    label      : 'Home Value',
    name       : FieldNames.homeValue,
  },
  {
    id       : 1003,
    ...SHARED[FieldNames.propertyType],
    component: Select,
    fieldPopover: true,
  },
  {
    id       : 1004,
    ...SHARED[FieldNames.propertyUsage],
    component: Select,
    fieldPopover: true,
  },
  {
    id        : 1005,
    component : SingleFutureDate,
    label     : 'Expected Closing Date',
    name      : FieldNames.expectedClosingDate,
    showIf    : [hasSignedAgreement],
    isOptional: true,
  },
  {
    id         : 1006,
    component  : Year,
    label      : 'Year Built',
    name       : FieldNames.builtYear,
    isOptional : true,
    showIf     : [hasSignedAgreement, hasOfferPending],
  },
  {
    id       : 1007,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.propertyStreet,
    title    : 'Where is the property located?',
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.propertyStreet,
    },
    showIf    : [hasSignedAgreement, hasOfferPending],
  },
  {
    id         : 1008,
    component  : Address,
    isOptional : true,
    label      : 'Street Address 2',
    name       : FieldNames.propertyStreet2,
    validator  : {
      type : 'isMailingAddress',
      param: FieldNames.propertyStreet2,
    },
    showIf     : [hasSignedAgreement, hasOfferPending],
  },
  {
    id        : 1009,
    component : Address,
    label     : 'City',
    name      : FieldNames.propertyCity,
    showIf    : [hasSignedAgreement, hasOfferPending],
  },
  {
    id        : 1010,
    component : State,
    label     : 'State',
    name      : FieldNames.propertyState,
    showIf    : [hasSignedAgreement, hasOfferPending],
  },
  {
    id        : 1011,
    component : ZipCode,
    label     : 'Zip Code',
    name      : FieldNames.propertyZip,
    showIf    : [hasSignedAgreement, hasOfferPending],
  },
  {
    id        : PURCHASE_TURBO_IS_SHOPPING_ZIP_CODE_QUESTION,
    component : ZipCode,
    title     : 'In what zip codes are you home shopping?',
    name      : FieldNames.propertyZip,
    showIf    : [isShopping],
  },
  {
    id : 1013,
    ...SHARED[FieldNames.creditScore],
    title: 'What is your estimated credit score?',
  },
  {
    id : 1014,
    ...SHARED[FieldNames.firstName],
  },
  {
    id : 1015,
    ...SHARED[FieldNames.middleName],
  },
  {
    id : 1016,
    ...SHARED[FieldNames.lastName],
  },
  {
    id : 1017,
    ...SHARED[FieldNames.suffixName],
  },
  {
    id : 1018,
    ...SHARED[FieldNames.email],
  },
  {
    id : 1019,
    ...SHARED[FieldNames.phone],
  },
  {
    id : 1020,
    ...SHARED[FieldNames.dateOfBirth],
  },
  {
    id         : 1021,
    component  : Currency,
    isOptional : true,
    label      : 'Annual Taxes',
    name       : FieldNames.yearTaxes,
  },
  {
    id         : 1022,
    component  : Currency,
    isOptional : true,
    label      : 'Annual Insurance',
    name       : FieldNames.yearInsure,
  },
  {
    id         : 1023,
    component  : Currency,
    isOptional : true,
    label      : 'Annual Homeowners Association Fees',
    name       : FieldNames.yearAssociate,
  },
  {
    id: 1024,
    ...SHARED[FieldNames.escrowUsage],
    options: YES_OR_NO_OPTIONS,
    title  : 'Will you have an escrow for insurance and taxes?',
  },
  {
    id : 1025,
    ...SHARED[FieldNames.maritalStatus],
  },
  {
    id       : 1026,
    component: AgesFFAComponent,
    name     : FieldNames.dependentsAges,
    title    : 'Do you have any Dependents?',
    showIf   : [hasShowAgesOfDependents],
  },
  {
    id : 1027,
    ...SHARED[FieldNames.citizenshipType],
  },
  {
    id : 1028,
    ...SHARED[FieldNames.militaryExperience],
  },
  {
    id        : 1029,
    component : RadioField,
    name      : FieldNames.livingRentOrOwn,
    options   : LIVING_SITUATION_OPTIONS,
  },
  {
    id: 1030,
    ...SHARED[FieldNames.propertyType],
    component    : Select,
    fieldPopover : true,
    name         : FieldNames.livingPropertyType,
  },
  {
    id        : 1031,
    component : Address,
    label     : 'Street Address',
    name      : FieldNames.livingPropertyStreet,
    validator : {
      type : 'isMailingAddress',
      param: FieldNames.livingPropertyStreet,
    },
  },
  {
    id        : 1032,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.livingPropertyStreet2,
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.livingPropertyStreet2,
    },
  },
  {
    id       : 1033,
    component: Address,
    label    : 'City',
    name     : FieldNames.livingPropertyCity,
  },
  {
    id       : 1034,
    component: State,
    label    : 'State',
    name     : FieldNames.livingPropertyState,
  },
  {
    id: 1035,
    ...SHARED[FieldNames.propertyZip],
    name  : FieldNames.livingPropertyZip,
  },
  {
    id       : 1036,
    component: Currency,
    label    : 'Annual Taxes',
    name     : FieldNames.livingYearTaxes,
  },
  {
    id       : 1037,
    component: Currency,
    label    : 'Annual Insurance',
    name     : FieldNames.livingYearInsure,
  },
  {
    id        : 1038,
    component : Currency,
    isOptional: true,
    label     : 'Annual Homeowners Association Fees',
    name      : FieldNames.livingYearAssociate,
  },
  {
    id       : 1039,
    component: SinglePastDate,
    label    : 'Occupancy Start Date',
    name     : FieldNames.livingOccupancyStart,
  },
  {
    id        : 1040,
    component : Address,
    label     : 'Street Address',
    name      : FieldNames.livingPrevPropertyStreet,
    showIf    : [isLessThanTwoYrsAtLivingAddress],
    title     : 'Previous Address',
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.livingPrevPropertyStreet,
    },
  },
  {
    id         : 1041,
    component  : Address,
    isOptional : true,
    label      : 'Street Address 2',
    name       : FieldNames.livingPrevPropertyStreet2,
    showIf     : [isLessThanTwoYrsAtLivingAddress],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.livingPrevPropertyStreet2,
    },
  },
  {
    id        : 1042,
    component : Address,
    label     : 'City',
    name      : FieldNames.livingPrevPropertyCity,
    showIf    : [isLessThanTwoYrsAtLivingAddress],
  },
  {
    id        : 1043,
    component : State,
    label     : 'State',
    name      : FieldNames.livingPrevPropertyState,
    showIf    : [isLessThanTwoYrsAtLivingAddress],
  },
  {
    ...SHARED[FieldNames.propertyZip],
    id     : 1044,
    name   : FieldNames.livingPrevPropertyZip,
    showIf : [isLessThanTwoYrsAtLivingAddress],
  },
  {
    id        : 1045,
    component : Select,
    name      : FieldNames.livingPrevRentOrOwn,
    options   : LIVING_SITUATION_OPTIONS,
    showIf    : [isLessThanTwoYrsAtLivingAddress],
    label     : 'Housing Situation',
  },
  {
    id       : 1046,
    component: SinglePastDate,
    label    : 'Occupancy Start Date',
    name     : FieldNames.livingPrevOccupancyStart,
    showIf   : [isLessThanTwoYrsAtLivingAddress],
    validator: {
      type : 'isBeforeEndDate',
      param: FieldNames.livingOccupancyStart,
    },
  },
  //@TODO update ids
  {
    id : 1047,
    ...SHARED[FieldNames.escrowUsage],
    name  : FieldNames.livingEscrowUsage,
    title : 'Do you have an escrow account?',
  },
  {
    id        : 1048,
    component : RadioField,
    name      : FieldNames.homeSoldYN,
    options   : YES_OR_NO_OPTIONS,
    title     : 'Do you plan to sell this home?',
  },
  {
    id       : 1049,
    component: Currency,
    label    : 'Monthly Rent Payment',
    name     : FieldNames.livingMonthlyRentAmount,
  },
  {
    id: 1050,
    component: RadioField,
    name     : FieldNames.coBorrowerYN,
    options  : YES_OR_NO_OPTIONS,
    title    : 'Will you have a co-borrower?',
  },
  {
    id: 1051,
    ...SHARED[FieldNames.firstName],
    name  : FieldNames.coBorrowerFirstName,
    title : 'Tell us about the co-borrower',
    showIf: [hasCoBorrower],
  },
  {
    id        : 1052,
    component : Name,
    label     : 'Middle Name',
    name      : FieldNames.coBorrowerMiddleName,
    isOptional: true,
    showIf    : [hasCoBorrower],
  },
  {
    id: 1053,
    ...SHARED[FieldNames.lastName],
    name  : FieldNames.coBorrowerLastName,
    showIf: [hasCoBorrower],
  },
  {
    id: 1154,
    ...SHARED[FieldNames.coBorrowerSuffixName],
  },
  {
    ...SHARED[FieldNames.email],
    id     : 1055,
    name   : FieldNames.coBorrowerEmail,
    showIf : [hasCoBorrower],
  },
  {
    ...SHARED[FieldNames.phone],
    id     : 1056,
    name   : FieldNames.coBorrowerPhone,
    showIf : [hasCoBorrower],
  },
  {
    id: 1057,
    ...SHARED[FieldNames.dateOfBirth],
    name  : FieldNames.coBorrowerDOB,
    showIf: [hasCoBorrower],
  },
  {
    id       : 1058,
    component: RadioField,
    name     : FieldNames.coBorrowerOnTitle,
    options  : YES_OR_NO_OPTIONS,
    title    : 'Will they be on the title?',
    showIf   : [hasCoBorrower],
  },
  {
    id        : 1059,
    component : RadioField,
    name      : FieldNames.coBorrowerSameAddress,
    options   : YES_OR_NO_OPTIONS,
    title     : 'Same address as borrower?',
  },
  {
    id        : 1060,
    component : Address,
    label     : 'Street Address',
    name      : FieldNames.coBorrowerLivingPropertyStreet,
    title     : `Co-borrower's current address?`,
    showIf    : [coBorrowerHasDifferentAddress],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.coBorrowerLivingPropertyStreet,
    },
  },
  {
    id         : 1061,
    component  : Address,
    isOptional : true,
    label      : 'Street Address 2',
    name       : FieldNames.coBorrowerLivingPropertyStreet2,
    showIf     : [coBorrowerHasDifferentAddress],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.coBorrowerLivingPropertyStreet2,
    },
  },
  {
    id        : 1062,
    component : Address,
    label     : 'City',
    name      : FieldNames.coBorrowerLivingPropertyCity,
    showIf    : [coBorrowerHasDifferentAddress],
  },
  {
    id        : 1063,
    component : State,
    label     : 'State',
    name      : FieldNames.coBorrowerLivingPropertyState,
    showIf    : [coBorrowerHasDifferentAddress],
  },
  {
    id        : 1064,
    component : ZipCode,
    label     : 'Zip Code',
    name      : FieldNames.coBorrowerLivingPropertyZip,
    showIf    : [coBorrowerHasDifferentAddress],
  },
  {
    id        : 1065,
    component : RadioField,
    title     : 'Co-borrower’s current housing situation?',
    name      : FieldNames.coBorrowerLivingRentOrOwn,
    options   : LIVING_SITUATION_OPTIONS,
    showIf    : [coBorrowerHasDifferentAddress],
  },
  {
    ...SHARED[FieldNames.maritalStatus],
    id     : 1066,
    name   : FieldNames.coBorrowerMaritalStatus,
    title  : `Co-borrower's marital status?`,
    showIf : [hasCoBorrower],
  },
  {
    id: 1067,
    ...SHARED[FieldNames.coBorrowerCitizenshipType],
  },
  {
    id: 1068,
    ...SHARED[FieldNames.coBorrowerMilitaryExperience],
  },
  {
    id        : 1069,
    component : LoanOfficerLanding,
    name      : FieldNames.loanOfficer,
  },
  {
    id        : 1070,
    component : ReoLiabilities,
    name      : FieldNames.realEstateLiabilities,
    options   : REAL_ESTATE_LIABILITY_OPTIONS,
  },
  {
    ...SHARED[FieldNames.hasAddlPropertiesYN],
    id     : 1071,
    showIf : [isHomeowner],
  },
  {
    id       : 1072,
    component: AdditionalProperties,
    name     : FieldNames.addlProperties,
    showIf   : [hasAddlProperties],
  },
  {
    id       : 1073,
    component: BankAccounts,
    options  : ACCOUNT_TYPE_PURCHASE_OPTIONS,
    name     : FieldNames.assets,
  },
  {
    id       : 1074,
    component: AssetSummary,
    name     : FieldNames.assetSummary,
  },
  {
    id        : 1075,
    name      : FieldNames.canVerifyOfIncomeEmployment,
    component : RadioField,
    options   : YES_OR_NO_OPTIONS,
  },{
    id        : 1076,
    component : FinancialVerificationConnection,
    name      : FieldNames.isFinancialVerificationComplete,
    showIf    : [hasFinancialVerificationIntegration],
  },
  {
    id       : 1077,
    component: RadioField,
    title    : 'What is your income source?',
    name     : FieldNames.employmentType,
    options  : EMPLOYMENT_TYPE_OPTIONS,
    disclaimer : INCOME_DISCLAIMER,
  },
  {
    id       : 1078,
    component: TextInput,
    label    : 'Company Name',
    name     : FieldNames.employCompanyName,
  },
  {
    id       : 1079,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.employStreet,
  },
  {
    id        : 1080,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.employStreet2,
  },
  {
    id       : 1081,
    component: Address,
    label    : 'City',
    name     : FieldNames.employCity,
  },
  {
    id       : 1082,
    component: State,
    label    : 'State',
    name     : FieldNames.employState,
  },
  {
    ...SHARED[FieldNames.propertyZip],
    id   : 1083,
    name : FieldNames.employZip,
  },
  {
    id         : 1084,
    component  : TextInput,
    label      : 'Job Title',
    isOptional : true,
    name       : FieldNames.employTitle,
  },

  {
    id       : 1085,
    component: SinglePastDate,
    label    : 'Start Date',
    name     : FieldNames.employStart,
  },
  {
    id       : 1086,
    component: Currency,
    label    : 'Monthly Base Pay',
    name     : FieldNames.employBase,
  },
  {
    id: 1087,
    ...SHARED[FieldNames.employPartyTransaction],
  },
  {
    id          : 1088,
    component   : Currency,
    fieldPopover: true,
    isOptional  : true,
    label       : 'Monthly Overtime',
    name        : FieldNames.employOvertime,
    popover     :
      'Overtime can be included in your income if it has been consistent over a two-year period.',
    title: 'Additional income sources?',
  },
  {
    id          : 1089,
    component   : Currency,
    fieldPopover: true,
    isOptional  : true,
    label       : 'Monthly Commission',
    name        : FieldNames.employCommission,
    popover     :
      'Commissions can be included in your income if they have been consistent over a two-year period.',
  },
  {
    id          : 1090,
    component   : Currency,
    fieldPopover: true,
    isOptional  : true,
    label       : 'Monthly Bonus',
    name        : FieldNames.employBonus,
    popover     :
      'Bonuses can be included in your income if they have been consistent over a two-year period.',
  },
  {
    id       : 1091,
    component: IncomeSources,
    name     : FieldNames.additionalIncome,
  },
  {
    id       : 1092,
    component: TextInput,
    label    : 'Previous Employers Name',
    name     : FieldNames.employPrevName,
    title    : 'Previous employment information',
    showIf   : [isLessThanTwoYrsAtJob],
    popover  :
      'Lenders will require previous employer information if you\'ve been with this employer for less than 2 years.',
  },
  {
    id       : 1093,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.employPrevStreet,
    showIf   : [isLessThanTwoYrsAtJob],
  },
  {
    id        : 1094,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.employPrevStreet2,
    showIf    : [isLessThanTwoYrsAtJob],
  },
  {
    id       : 1095,
    component: Address,
    label    : 'City',
    name     : FieldNames.employPrevCity,
    showIf   : [isLessThanTwoYrsAtJob],
  },
  {
    id       : 1096,
    component: State,
    label    : 'State',
    name     : FieldNames.employPrevState,
    showIf   : [isLessThanTwoYrsAtJob],
  },
  {
    id       : 1097,
    component: ZipCode,
    label    : 'Zip',
    name     : FieldNames.employPrevZip,
    showIf   : [isLessThanTwoYrsAtJob],
  },
  {
    id: 1098,
    ...SHARED[FieldNames.employPrevTitle],
  },
  {
    id       : 1099,
    component: SinglePastDate,
    label    : 'Start Date',
    name     : FieldNames.employPrevStart,
    showIf   : [isLessThanTwoYrsAtJob],
  },
  {
    id       : 1100,
    component: SinglePastDate,
    label    : 'End Date',
    name     : FieldNames.employPrevEnd,
    showIf   : [isLessThanTwoYrsAtJob],
    validator: {
      type : 'isAfterStartDate',
      param: [FieldNames.employPrevStart, FieldNames.employStart],
    },
  },
  {
    id: 1101,
    ...SHARED[FieldNames.employOwnershipShare],
  },
  {
    id       : 1102,
    component: RetirementIncomeSources,
    title    : 'Tell us about your retirement income',
    name     : FieldNames.retirementIncome,
    showIf   : [isRetired],
  },
  {
    id       : 1103,
    component: OtherIncomeSources,
    title    : 'Tell us about your other income',
    name     : FieldNames.otherIncome,
    showIf   : [isEmploymentOther],
  },
  {
    id        : 1104,
    component : RadioField,
    name      : FieldNames.coBorrowerEmploymentType,
    options   : COBORROWER_EMPLOYMENT_TYPE_OPTIONS,
  },
  {
    id        : 1105,
    component : TextInput,
    title     : 'Tell us about your co-borrower\'s income and employment',
    label     : 'Company Name',
    name      : FieldNames.coBorrowerEmployCompanyName,
    showIf: [isCoEmployee, isCoSelfEmployed],
  },
  {
    id        : 1106,
    component : Address,
    label     : 'Street Address',
    name      : FieldNames.coBorrowerEmployStreet,
    showIf: [isCoEmployee, isCoSelfEmployed],
  },
  {
    id         : 1107,
    component  : Address,
    isOptional : true,
    label      : 'Street Address 2',
    name       : FieldNames.coBorrowerEmployStreet2,
    showIf: [isCoEmployee, isCoSelfEmployed],
  },
  {
    id        : 1108,
    component : Address,
    label     : 'City',
    name      : FieldNames.coBorrowerEmployCity,
    showIf: [isCoEmployee, isCoSelfEmployed],
  },
  {
    id        : 1109,
    component : State,
    label     : 'State',
    name      : FieldNames.coBorrowerEmployState,
    showIf: [isCoEmployee, isCoSelfEmployed],
  },
  {
    id        : 1110,
    component : ZipCode,
    label     : 'Zip Code',
    name      : FieldNames.coBorrowerEmployZip,
    showIf: [isCoEmployee, isCoSelfEmployed],
  },
  {
    id         : 1111,
    component  : TextInput,
    label      : 'Job Title',
    isOptional : true,
    name       : FieldNames.coBorrowerEmployTitle,
    showIf: [isCoEmployee, isCoSelfEmployed],
  },
  {
    id        : 1112,
    component : SinglePastDate,
    label     : 'Start Date',
    name      : FieldNames.coBorrowerEmployStart,
    showIf: [isCoEmployee, isCoSelfEmployed],
  },
  {
    id        : 1113,
    component : Currency,
    label     : 'Monthly Base Pay',
    name      : FieldNames.coBorrowerEmployBase,
    showIf: [isCoEmployee, isCoSelfEmployed],
  },
  {
    id: 1114,
    ...SHARED[FieldNames.coBorrowerEmployPartyTransaction],

  },
  {
    id           : 1115,
    component    : Currency,
    fieldPopover : true,
    isOptional   : true,
    label        : 'Monthly Overtime',
    name         : FieldNames.coBorrowerEmployOvertime,
    title        : 'Additional income sources?',
    popover      : 'Overtime can be included in your income if it has been consistent over a two-year period.',
    showIf: [isCoEmployee, isCoSelfEmployed],
  },
  {
    id           : 1116,
    component    : Currency,
    fieldPopover : true,
    isOptional   : true,
    label        : 'Monthly Commission',
    name         : FieldNames.coBorrowerEmployCommission,
    popover      : 'Commissions can be included in your income if they have been consistent over a two-year period.',
    showIf: [isCoEmployee, isCoSelfEmployed],
  },
  {
    id           : 1117,
    component    : Currency,
    fieldPopover : true,
    isOptional   : true,
    label        : 'Monthly Bonus',
    name         : FieldNames.coBorrowerEmployBonus,
    popover      : 'Bonuses can be included in your income if they have been consistent over a two-year period.',
    showIf: [isCoEmployee, isCoSelfEmployed],
  },
  {
    id        : 1118,
    component : IncomeSources,
    name      : FieldNames.coBorrowerAdditionalIncome,
    showIf    : [(values) => !isCoBorrowerEmploymentOther(values) && !isCoBorrowerRetired(values)],
  },
  {
    id        : 1119,
    component : TextInput,
    label     : 'Previous Employers Name',
    name      : FieldNames.coBorrowerEmployPrevName,
    title     : 'Previous employment information',
    showIf    : [isCoLessThanTwoYrsAtJob],
    popover   : `Lenders will require previous employer information if you've been with this employer for less than 2 years.`,
  },
  {
    id        : 1120,
    component : Address,
    label     : 'Street Address',
    name      : FieldNames.coBorrowerEmployPrevStreet,
    showIf    : [isCoLessThanTwoYrsAtJob],
  },
  {
    id         : 1121,
    component  : Address,
    isOptional : true,
    label      : 'Street Address 2',
    name       : FieldNames.coBorrowerEmployPrevStreet2,
    showIf     : [isCoLessThanTwoYrsAtJob],
  },
  {
    id        : 1122,
    component : Address,
    label     : 'City',
    name      : FieldNames.coBorrowerEmployPrevCity,
    showIf    : [isCoLessThanTwoYrsAtJob],
  },
  {
    id        : 1123,
    component : State,
    label     : 'State',
    name      : FieldNames.coBorrowerEmployPrevState,
    showIf    : [isCoLessThanTwoYrsAtJob],
  },
  {
    id        : 1124,
    component : ZipCode,
    label     : 'Zip',
    name      : FieldNames.coBorrowerEmployPrevZip,
    showIf    : [isCoLessThanTwoYrsAtJob],
  },
  {
    id: 1125,
    ...SHARED[FieldNames.employPrevTitle],
    name      : FieldNames.coBorrowerEmployPrevTitle,
    showIf    : [isCoLessThanTwoYrsAtJob],
  },
  {
    id        : 1126,
    component : SinglePastDate,
    label     : 'Start Date',
    name      : FieldNames.coBorrowerEmployPrevStart,
    showIf    : [isCoLessThanTwoYrsAtJob],
  },
  {
    id        : 1127,
    component : SingleDate,
    label     : 'End Date',
    name      : FieldNames.coBorrowerEmployPrevEnd,
    showIf    : [isCoLessThanTwoYrsAtJob],
    validator: {
      type : 'isAfterStartDate',
      param: [FieldNames.coBorrowerEmployPrevStart, FieldNames.coBorrowerEmployStart],
    },
  },
  {
    id        : 1128,
    component : RetirementIncomeSources,
    title     : `Tell us about your co-borrower's retirement income`,
    name      : FieldNames.coBorrowerRetirementIncome,
    showIf    : [isCoBorrowerRetired],
  },
  {
    id        : 1129,
    component : OtherIncomeSources,
    title     : `Tell us about your co-borrower's other income`,
    name      : FieldNames.coBorrowerOtherIncome,
    showIf    : [isCoBorrowerEmploymentOther],
  },
  {
    id: 1130,
    ...SHARED[FieldNames.coBorrowerEmployOwnershipShare],
  },
  {
    id: 1131,
    ...SHARED[FieldNames.propertyType],
    component    : Select,
    fieldPopover : true,
    name         : FieldNames.coBorrowerLivingPropertyType,
    showIf       : [coBorrowerHasDifferentAddress],
  },
  {
    id        : PURCHASE_TURBO_COLLECT_SSN_QUESTION,
    component : CollectRequiredSsn,
    name      : FieldNames.ssnQuestionRequirement,
  },
  {
    id        : PURCHASE_TURBO_COLLECT_SSN_QUESTION_FINAL,
    component : CollectConditionallyRequiredSsn,
    name      : FieldNames.ssnQuestionRequirement,
  },
]);
